import { TSupply, TVessel } from "~/types";
import { baseService } from "../base";
import { FilterParams, PaginationParams, PaginationResponse } from "../type";
import {
  ClockInParams,
  CreateStaffParams,
  CreateVesselParams,
  GetActivity,
  GetCurrentStatusActivity,
  GetStaffParams,
  UpdateStaffParams,
} from "./operation.types";
import { UpdateVesselParams } from "../vessel/vessel.types";
import { TStaff } from "~/types/staff";

export const operationServices = {
  getAllStaff: async (params?: GetStaffParams) => {
    const { data } = await baseService.get<PaginationResponse<TStaff>>(
      "/staffs",
      {
        params: {
          ...params,
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 500, // Max 500 per backend validation
          getAll: true, // Skip expensive joins (service tickets, etc.) - fixes 120s timeout
        },
      }
    );
    return data;
  },
  importStaffs: async (formData: FormData) => {
    const { data } = await baseService.post("/staffs/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  },
  getStaffById: async (id: string) => {
    const { data } = await baseService.get<TStaff>(`/staffs/${id}`);
    return data;
  },
  getStaffActivityToday: async (id: string) => {
    const { data } = await baseService.get<GetActivity>(
      `/staffs/${id}/today-activities`
    );
    return data;
  },
  getStaffCurrentStatusActivity: async (id: string) => {
    const { data } = await baseService.get<GetCurrentStatusActivity>(
      `/staffs/${id}/current-status`
    );
    return data;
  },
  getStaffWeeklyActivities: async (id: string, startDate?: string) => {
    const { data } = await baseService.get<GetActivity>(
      `/staffs/${id}/weekly-activities`,
      {
        params: startDate ? { startDate } : undefined,
      }
    );
    return data;
  },
  getStaffAvailabilityDetails: async (id: string, startDate?: string, endDate?: string) => {
    const { data } = await baseService.get(
      `/staffs/${id}/availability-details`,
      {
        params: { startDate, endDate },
      }
    );
    return data;
  },
  clockIn: async ({ id, ...body }: Partial<ClockInParams> & { id: string }) => {
    const { data } = await baseService.post(`/staffs/${id}/clock-in`, body);
    return data;
  },
  clockOut: async (id: string) => {
    const { data } = await baseService.post(`/staffs/${id}/clock-out`);
    return data;
  },
  createStaff: async (body: CreateStaffParams) => {
    const { data } = await baseService.post("/staffs", body);
    return data;
  },
  deleteStaff: async (id: string) => {
    const { data } = await baseService.delete(`/staffs/${id}`);
    return data;
  },
  updateStaff: async ({
    id,
    ...body
  }: Partial<UpdateStaffParams> & { id: string }) => {
    const { data } = await baseService.patch(`/staffs/${id}`, body);
    return data;
  },

  // Vessel
  getAllVessel: async (params?: FilterParams & PaginationParams) => {
    const { data } = await baseService.get<PaginationResponse<TVessel>>(
      "/vessels",
      {
        params: params ?? {
          page: 1,
          pageSize: 50,
        },
      }
    );
    return data;
  },
  createVessel: async (body: CreateVesselParams) => {
    const { data } = await baseService.post("/vessels", body);
    return data;
  },
  deleteVessel: async (id: string) => {
    const { data } = await baseService.delete(`/vessels/${id}`);
    return data;
  },
  updateVessel: async ({
    id,
    ...body
  }: UpdateVesselParams & { id: string }) => {
    const { data } = await baseService.patch(`/vessels/${id}`, body);
    return data;
  },

  // Supplies
  getAllSupplies: async (params?: FilterParams & PaginationParams) => {
    const { data } = await baseService.get<PaginationResponse<TSupply>>(
      "/supplies",
      {
        params: params ?? {
          page: 1,
          pageSize: 50,
        },
      }
    );
    return data;
  },
  createSupply: async (body: TSupply) => {
    const { data } = await baseService.post("/supplies", body);
    return data;
  },
  deleteSupply: async (id: string) => {
    const { data } = await baseService.delete(`/supplies/${id}`);
    return data;
  },
  updateSupply: async ({ id, ...body }: Partial<TSupply> & { id: string }) => {
    const { data } = await baseService.patch(`/supplies/${id}`, body);
    return data;
  },
};
